import * as React from "react";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {RestDataLoader} from "../../../dashboard/widgets/unilever/RestDataLoader";
import {Button, NumberInput, Tooltip} from "@vebox/etui-core";
import {Popup} from "../dashboard/lavazza/components/popup/Popup";
import {PopupController} from "../dashboard/lavazza/components/popup/PopupController";
import {VeBoxModal} from "../../../components/modal/VeBoxModal";
import {getLocaleDecimalSeparator, INPUT_BORDER_BOX} from "../../../components/common/etui_components_styles";
import {Display} from "../../../components/Display";
import {Loader} from "../../../components/Utils/loader/Loader";
import {AnimationCurves, VeboxAnimator} from "../../../animation/VeboxAnimation";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {lerpColor} from "../../../dashboard/operativity/charts/common";

import "./ProductStockLevel.scss";
import {background} from "@vebox/etui-system";

declare var AppTranslations: AppTranslations;

enum UnitOfMeasure {
    LITERS = "LITERS",
}

enum StockAlarmSeverity { LOW = "LOW", CRITICAL = "CRITICAL"}

interface ProductStock {
    capacity: number;
    stockLevel: number;
    stockPercent: number;
    unitOfMeasure: UnitOfMeasure;
    stockAlarmSeverity?: StockAlarmSeverity;
    configured: boolean;
    lastUpdate: string;
    productStockLevelInfo: ProductStockLevelInfo;
    stockLevelThresholdInfo: StockLevelThresholdInfo;
}

interface StockLevelThresholdInfo {
    criticalAlarmInfo: string;
    lowAlarmInfo: string;
    criticalAlarmThreshold: number;
    lowAlarmThreshold: number;
}

interface ProductStockLevelInfo {
    guid: string;
    name: string;
    logo: string;
    bigLogo: string;
    backgroundColor1: string;
    backgroundColor2: string;
}

interface ProductsStockLevelProps {
    equipmentGuid: string;
    editable: boolean;
}

const popupController = new PopupController();

const DATA_POLLING_INTERVAL = 10000;

export const ProductsStockLevel: React.FC<ProductsStockLevelProps> = (props) => {

    const [products, setProducts] = React.useState<ProductStock[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [hasError, setHasError] = React.useState<boolean>(false);

    const loadProductData = () => {
        return getProductStocks(props.equipmentGuid)
            .then((p) => p.sort((a, b) => Number(b.configured) - Number(a.configured)))
            .then(setProducts)
            .catch(() => setHasError(true));
    };

    React.useEffect(() => {
        loadProductData()
            .then(() => setIsLoading(false));

        const id = setInterval(
            loadProductData,
            DATA_POLLING_INTERVAL
        );

        return () => {
            clearInterval(id);
        };
    }, []);

    return (
        <>
            <Display isVisible={!hasError || (products.length > 0)}>
                <VeBoxCard style={{width: "100%"}}>
                    <VeBoxCard.Header title={AppTranslations.productStockLevel.sectionTitle}/>
                    <VeBoxCard.Body
                        className={"ProductStockLevel"}
                        style={{position: "relative", height: isLoading ? 100 : undefined}}
                    >
                        <Display isVisible={!isLoading}>
                            <div className={"flx-col"}>
                                {
                                    products
                                        .map((p, i) => (
                                            p.configured
                                                ? <ProductViewRow
                                                    key={p.productStockLevelInfo.guid}
                                                    product={p}
                                                    equipmentGuid={props.equipmentGuid}
                                                    onUpdate={loadProductData}
                                                    editable={props.editable}
                                                />
                                                : <UnmonitoredProductViewRow
                                                    key={p.productStockLevelInfo.guid}
                                                    product={p}
                                                    equipmentGuid={props.equipmentGuid}
                                                    onUpdate={loadProductData}
                                                    editable={props.editable}
                                                />
                                        ))
                                }
                            </div>
                        </Display>
                    </VeBoxCard.Body>
                    <Popup controller={popupController}/>
                </VeBoxCard>
            </Display>
        </>
    );
}

interface ProductViewRowProps {
    equipmentGuid: string;
    product: ProductStock;
    editable: boolean;
    onUpdate: () => void;
}

const ProductViewRow: React.FC<ProductViewRowProps> = (props) => {

    const executeAction = (action: ProductActionModalAction) => {
        switch (action) {
            case ProductActionModalAction.EDIT_STOCK_LEVEL: {

                const editStockLevel = (newStockLevel: number) => {
                    popupController.closePopup();
                    updateProductCurrentStockLevel(props.equipmentGuid, props.product.productStockLevelInfo.guid, String(newStockLevel))
                        .then(props.onUpdate);
                };

                const close = () => {
                    popupController.closePopup();
                };

                const isValid = (level: number) => level >= 0 && level <= props.product.capacity;

                popupController.showPopup(
                    <EditFieldModal
                        title={
                            insertHTMLInString(
                                AppTranslations.productStockLevel.editProductLevelTitle,
                                [
                                    <span className={"bold"} key={0}>{props.product.productStockLevelInfo.name}</span>
                                ]
                            )
                        }
                        editTextLabel={AppTranslations.productStockLevel.editProductLevelPlaceholder}
                        onSubmit={editStockLevel}
                        isValid={isValid}
                        onCancel={close}
                    >
                        {insertHTMLInString(
                            AppTranslations.productStockLevel.editProductLevelInfo,
                            [
                                <span className={"bold"} key={0}>
                                    {formatNumber(props.product.stockLevel)} {getUnitOfMeasureLabel(props.product.unitOfMeasure)}
                                </span>,
                                formatNumber(props.product.stockPercent) + "%"
                            ]
                        )}
                    </EditFieldModal>,
                    close
                );

                break;
            }
            case ProductActionModalAction.EDIT_TANK_CAPACITY: {
                const editStockCapacity = (newCapacity: number) => {
                    popupController.closePopup();
                    updateProductTankCapacity(props.equipmentGuid, props.product.productStockLevelInfo.guid, String(newCapacity))
                        .then(props.onUpdate);
                };

                const close = () => {
                    popupController.closePopup();
                };

                const isValidCapacity = (capacity: number) => capacity >= props.product.stockLevel && capacity >= 0;

                popupController.showPopup(
                    <EditFieldModal
                        title={
                            insertHTMLInString(
                                AppTranslations.productStockLevel.editProductCapacityTitle,
                                [
                                    <span className={"bold"} key={0}>{props.product.productStockLevelInfo.name}</span>
                                ]
                            )
                        }
                        editTextLabel={"New capacity"}
                        onSubmit={editStockCapacity}
                        isValid={isValidCapacity}
                        onCancel={close}
                    >
                        {
                            insertHTMLInString(
                                AppTranslations.productStockLevel.editProductCapacityInfo,
                                [
                                    <span className={"bold"} key={0}>
                                        {props.product.capacity} {getUnitOfMeasureLabel(props.product.unitOfMeasure)}
                                    </span>
                                ]
                            )
                        }
                    </EditFieldModal>,
                    close
                );

                break;
            }
            case ProductActionModalAction.REFILL: {
                const refill = (refillAmount: number) => {
                    popupController.closePopup();
                    refillProduct(props.equipmentGuid, props.product.productStockLevelInfo.guid, refillAmount)
                        .then(props.onUpdate);
                };

                const close = () => {
                    popupController.closePopup();
                };

                const isValid = (refillAmount: number) => {
                    return refillAmount > 0
                        && refillAmount <= (props.product.capacity - props.product.stockLevel);
                };

                const title = insertHTMLInString(
                    AppTranslations.productStockLevel.refillModalTitle,
                    [
                        <span className={"bold"} key={0}>{props.product.productStockLevelInfo.name}</span>
                    ]
                );

                const question = insertHTMLInString(
                    AppTranslations.productStockLevel.preRefillModalQuestion,
                    [
                        <span className={"bold"} key={0}>
                            {formatNumber(props.product.stockLevel)} {getUnitOfMeasureLabel(props.product.unitOfMeasure)}
                        </span>,
                        formatNumber(props.product.stockPercent) + "%"
                    ]
                );

                const openRefillModal = () => {
                    popupController.showPopup(
                        <EditFieldModal
                            title={title}
                            editTextLabel={AppTranslations.productStockLevel.refillModalPlaceholder}
                            isValid={isValid}
                            onSubmit={refill}
                            onCancel={close}
                        />,
                        close
                    );
                };

                const sendFeedback = (correctStockLevel: number) => {
                    sendStockLevelFeedback(props.equipmentGuid, props.product.productStockLevelInfo.guid, String(correctStockLevel))
                        .then(openRefillModal);
                };

                const isValidCapacity = (capacity: number) => capacity >= props.product.stockLevel && capacity <= props.product.capacity;

                const openStockLevelCorrectionModal = () => {

                    popupController.showPopup(
                        <NewSingleValueModal
                            title={title}
                            editTextLabel={AppTranslations.productStockLevel.preRefillModalPlaceholder}
                            question={question}
                            onCancel={openRefillModal}
                            onSubmit={sendFeedback}
                            isValid={isValidCapacity}
                        />,
                        close
                    );

                }

                popupController.showPopup(
                    <QuestionModal
                        title={title}
                        onYes={openRefillModal}
                        onNo={openStockLevelCorrectionModal}
                    >
                        {question}
                    </QuestionModal>,
                    close
                );


                break;
            }
            case ProductActionModalAction.DELETE: {
                popupController.closePopup();
                toggleProductMonitoring(props.equipmentGuid, props.product.productStockLevelInfo.guid, "false")
                    .then(props.onUpdate);
                break;
            }
        }
    };

    const closePopup = () => {
        popupController.closePopup();
    };

    const openProductActionModal = () => {
        popupController.showPopup(
            <ProductActionModal
                productName={props.product.productStockLevelInfo.name}
                onClose={closePopup}
                onAction={executeAction}
            />,
            closePopup
        )
    };

    const bgBrightness = brightness(props.product.productStockLevelInfo.backgroundColor1);
    return (
        <div className={"padding-5"}>
            <div
                className={"rounded-outlined padding-5" + (props.editable ? " cursor-pointer elevate" : "")}
                onClick={props.editable ? openProductActionModal : undefined}
            >
                <div className={"flx-row flx-align-center product-view-row"}>
                    <ProductDetailView product={props.product}/>
                    <Display isVisible={props.product.stockPercent > 0}>
                        <VerticalDivider
                            bgColor={props.product.productStockLevelInfo.backgroundColor1}
                            dividerColor={bgBrightness > 186 ? "black" : "white"}
                            dividerHeight={80}
                            width={2}
                        />
                    </Display>
                    <ProductPercentageBar product={props.product}/>
                </div>
            </div>
        </div>
    );
}

interface VerticalDivider {
    bgColor?: string;
    dividerColor: string;
    dividerHeight: string | number;
    width?: number;
}

const VerticalDivider: React.FC<VerticalDivider> = (props) => {
    return (
        <div className={"flex flx-align-center"} style={{height: props.dividerHeight, background: props.bgColor}}>
            <div className={"vertical-divider"} style={{background: props.dividerColor, width: props.width}}/>
        </div>
    );
}

interface UnmonitoredProductViewRowProps {
    product: ProductStock;
    editable: boolean;
    equipmentGuid: string;
    onUpdate: () => void;
}

const UnmonitoredProductViewRow: React.FC<UnmonitoredProductViewRowProps> = (props) => {

    const addProduct = (stockLevel: number, capacity?: number) => {
        popupController.closePopup();
        createProductStock(
            props.equipmentGuid,
            {
                ...props.product,
                capacity: !capacity ? props.product.capacity : capacity,
                stockLevel,
            },
        ).then(props.onUpdate);
    }

    const onCancel = () => {
        popupController.closePopup();
    }

    const openModal = () => {
        popupController.showPopup(
            <AddProductMonitorModal
                productName={props.product.productStockLevelInfo.name}
                stockCapacity={props.product.capacity}
                unitOfMeasure={props.product.unitOfMeasure}
                onAdd={addProduct}
                onCancel={onCancel}
            />,
            popupController.closePopup
        );
    };

    return (
        <div className={"padding-5"}>
            <div
                className={"rounded-outlined flex flx-align-center flx-space-between" + (props.editable ? " cursor-pointer elevate" : undefined)}
                style={{padding: "5px 10px"}}
                onClick={props.editable ? openModal : undefined}
            >
                <UnmonitoredProductDetailView product={props.product}/>
                <Display isVisible={props.editable}>
                    <div className={"add-product-icon"}>
                        <span className={"material-icons"}>add</span>
                    </div>
                </Display>
            </div>
        </div>
    );
}

interface AddProductMonitorModalProps {
    stockCapacity: number;
    productName: string;
    unitOfMeasure: UnitOfMeasure;
    onCancel: () => void;
    onAdd: (level: number, capacity?: number) => void;
}

const AddProductMonitorModal: React.FC<AddProductMonitorModalProps> = (props) => {

    const [stockCapacity, setStockCapacity] = React.useState(props.stockCapacity);
    const [stockLevel, setStockLevel] = React.useState<number>();

    const updateStockCapacity = (x: any) => {
        if (!isNaN(x)) {
            setStockCapacity(Number(x));
        }
    };

    const updateStockLevel = (x: any) => {
        if (!isNaN(x)) {
            setStockLevel(Number(x));
        }
    };

    const submit = () => {
        props.onAdd(stockLevel, stockCapacity);
    };

    return (
        <VeBoxModal>
            <VeBoxModal.Header
                title={insertHTMLInString(
                    AppTranslations.productStockLevel.addProductTitle,
                    [
                        <span className={"bold"} key={0}>{props.productName}</span>
                    ]
                )}
            />
            <VeBoxModal.Body>
                <VeBoxModal.Section>
                    <p style={{marginBottom: 10, fontSize: 16}}>
                        {insertHTMLInString(
                            AppTranslations.productStockLevel.addProductModalDescription,
                            [
                                <span className={"bold"} key={0}>{props.productName}</span>
                            ]
                        )}
                    </p>
                    <ProductValueEditingBox
                        name={"stockCapacity"}
                        label={AppTranslations.productStockLevel.addProductCapacityPlaceholder}
                        value={stockCapacity}
                        onChange={updateStockCapacity}
                        unitOfMeasure={props.unitOfMeasure}
                        readModePlaceholder={
                            <p style={{marginBottom: 10}}>{
                                insertHTMLInString(
                                    AppTranslations.productStockLevel.addProductCapacityDescription,
                                    [
                                        <span
                                            className={"bold"}
                                            key={0}
                                        >{props.stockCapacity} {getUnitOfMeasureLabel(props.unitOfMeasure)}</span>
                                    ]
                                )
                            }</p>
                        }
                        editing={false}
                    />
                    <ProductValueEditingBox
                        name={"stockLevel"}
                        label={AppTranslations.productStockLevel.addProductStockLevelPlaceholder}
                        value={stockLevel}
                        onChange={updateStockLevel}
                        unitOfMeasure={props.unitOfMeasure}
                        editing={true}
                    />
                </VeBoxModal.Section>
            </VeBoxModal.Body>
            <VeBoxModal.StandardActions
                onSubmit={submit}
                canSubmit={stockLevel >= 0 && stockLevel <= stockCapacity}
                onCancel={props.onCancel}
            />
        </VeBoxModal>
    )
}

interface ProductValueEditingBoxProps {
    name: string;
    label: string;
    unitOfMeasure: UnitOfMeasure;
    value: number;
    editing?: boolean;
    readModePlaceholder?: JSX.Element | string;
    onChange: (value: any) => void;
}

const ProductValueEditingBox: React.FC<ProductValueEditingBoxProps> = (props) => {

    const [inEditing, setEditing] = React.useState(props.editing ?? true);

    const toggleEditing = () => setEditing(prevInEditing => !prevInEditing)

    return inEditing
        ? (
            <div className={"flx-row flx-space-between flx-align-center"} style={{marginBottom: 10}}>
                <NumberInput
                    name={props.name}
                    value={props.value}
                    size="small"
                    label={props.label}
                    decimalSeparator={getLocaleDecimalSeparator()}
                    onChange={props.onChange}
                    style={{width: 150}}
                    styles={{
                        ...(INPUT_BORDER_BOX as any),
                        root: {
                            width: 250
                        }
                    }}
                />
                {getUnitOfMeasureLabel(props.unitOfMeasure)}
            </div>
        )
        : (
            <div className={"flx-row flx-space-between flx-align-center"} style={{marginBottom: 10}}>
                {props.readModePlaceholder}
                <Button onClick={toggleEditing}>
                    <span className="material-icons-outlined">mode_edit</span>
                </Button>
            </div>
        )
}

const ProductDetailView: React.FC<{ product: ProductStock }> = (props) => {
    const bgBrightness = brightness(props.product.productStockLevelInfo.backgroundColor1);
    return (
        <div
            className={"flx-row flx-align-center product-detail-view"}
            style={{
                background: props.product.productStockLevelInfo.backgroundColor1,
                color: bgBrightness > 186 ? "black" : "white",
                borderRadius: props.product.stockPercent === 0 ? "4px" : undefined,
            }}
        >
            <img
                src={props.product.productStockLevelInfo.logo}
                style={{backgroundColor: props.product.productStockLevelInfo.backgroundColor1}}
                className={"product-logo"}
            />
            <div>
                <div className={"product-name"}>
                    {props.product.productStockLevelInfo.name}
                </div>
            </div>
        </div>
    );
}

const UnmonitoredProductDetailView: React.FC<{ product: ProductStock }> = (props) => {
    return (
        <div className={"flx-row flx-align-center product-detail-view-unmonitored"}>
            <img
                src={props.product.productStockLevelInfo.logo}
                style={{backgroundColor: props.product.productStockLevelInfo.backgroundColor1}}
                className={"product-logo"}
            />
            <div>
                <div className={"product-name"}>
                    {props.product.productStockLevelInfo.name}
                </div>
                <p className={"not-monitored-label"}>
                    {AppTranslations.productStockLevel.notMonitored}
                </p>
            </div>
        </div>
    );
}

interface ProductPercentageBarProps {
    product: ProductStock;
}

const BLACK_TO_WHITE_THRESHOLD = 186;
const MIN_BAR_WIDTH = 150;

const ANIMATION_DURATION = 600; // ms

const ProductPercentageBar: React.FC<ProductPercentageBarProps> = (props) => {
    const barRef = React.useRef<HTMLDivElement>();

    const prevPercentage = usePrevious(props.product.stockPercent);

    return (
        <div className={"flx-row flx-align-center"} style={{width: "100%", position: "relative"}}>
            <VeboxAnimator
                from={prevPercentage ?? 0}
                to={props.product.stockPercent}
                curve={AnimationCurves.easeOutCubic}
                duration={ANIMATION_DURATION}
            >
                {(percentage) => {
                    const isBarBigEnough = (barRef?.current?.clientWidth ?? 0) >= MIN_BAR_WIDTH;
                    const bg2 = lerpColor(props.product.productStockLevelInfo.backgroundColor1, props.product.productStockLevelInfo.backgroundColor2, percentage / 100);
                    const bgBrightness = brightness(bg2);
                    return (
                        <div className={"flx-row flx-align-center product-percentage-bar-bg"}>
                            <div
                                className={"product-percentage-bar flex flx-align-center"}
                                ref={barRef}
                                style={{
                                    width: percentage + "%",
                                    backgroundImage: "linear-gradient(to right," + props.product.productStockLevelInfo.backgroundColor1 + " , " + bg2 + ")",
                                    color: bgBrightness > BLACK_TO_WHITE_THRESHOLD ? "black" : "white",
                                    padding: !isBarBigEnough ? 0 : undefined
                                }}
                            >
                                <Display isVisible={isBarBigEnough}>
                                    <div className={"product-value"}>
                                        <h2 className={"percentage-label"}>{Math.floor(percentage)}%</h2>
                                        <p className={"subtext"}>
                                            {formatNumber(props.product.stockLevel)} / {formatNumber(props.product.capacity)} {getUnitOfMeasureLabel(props.product.unitOfMeasure)}
                                        </p>
                                    </div>
                                </Display>
                            </div>
                            <Display isVisible={!isBarBigEnough}>
                                <div className={"product-value"}>
                                    <h2 className={"percentage-label"}>{Math.floor(percentage)}%</h2>
                                    <p className={"subtext"}>
                                        {formatNumber(props.product.stockLevel)} / {formatNumber(props.product.capacity)} {getUnitOfMeasureLabel(props.product.unitOfMeasure)}
                                    </p>
                                </div>
                            </Display>
                        </div>
                    );
                }}
            </VeboxAnimator>
            <div className={"alarm-bell-container"}>
                <AlarmBell
                    alarmStatus={props.product.stockAlarmSeverity}
                    alarmInfo={props.product.stockLevelThresholdInfo}
                />
            </div>
        </div>
    );
}

function usePrevious<T>(value: T) {
    const ref = React.useRef<T>();
    React.useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

interface AlarmBellProps {
    alarmStatus: StockAlarmSeverity;
    alarmInfo: StockLevelThresholdInfo;
}

const AlarmBell: React.FC<AlarmBellProps> = (props) => {
    switch (props.alarmStatus) {
        case StockAlarmSeverity.CRITICAL: {
            return (
                <Tooltip placement={"top"} message={props.alarmInfo.criticalAlarmInfo}>
                    <div className={"alarm-bell critical-alarm"}>
                        <span className={"material-icons"}>notification_important</span>
                    </div>
                </Tooltip>
            );
        }
        case StockAlarmSeverity.LOW: {
            return (
                <Tooltip placement={"top"} message={props.alarmInfo.lowAlarmInfo}>
                    <div className={"alarm-bell low-alarm"}>
                        <span className={"material-icons"}>notifications</span>
                    </div>
                </Tooltip>
            );
        }
        default:
            return null;
    }
}

enum ProductActionModalAction {
    REFILL,
    EDIT_STOCK_LEVEL,
    EDIT_TANK_CAPACITY,
    DELETE
}

interface ProductActionModal {
    productName: string;
    onClose: () => void;
    onAction: (action: ProductActionModalAction) => void;
}

const ProductActionModal: React.FC<ProductActionModal> = (props) => {
    const onAction = (action: ProductActionModalAction) => () => props.onAction(action);
    return (
        <VeBoxModal>
            <VeBoxModal.Header title={<span className={"bold"}>{props.productName}</span>} onClose={props.onClose}/>
            <VeBoxModal.Body>
                <VeBoxModal.Section className={"flx-col"}>
                    <button
                        className={"rounded-outlined flx-row flx-align-center modal-option-buttons"}
                        onClick={onAction(ProductActionModalAction.REFILL)}
                    >
                        <span className="material-icons-outlined">opacity</span>
                        {AppTranslations.productStockLevel.refillProductButton}
                    </button>
                    <button
                        className={"rounded-outlined flx-row flx-align-center modal-option-buttons"}
                        onClick={onAction(ProductActionModalAction.EDIT_STOCK_LEVEL)}
                    >
                        <span className="material-icons-outlined">mode_edit</span>
                        {AppTranslations.productStockLevel.editProductLevelButton}
                    </button>
                    <button
                        className={"rounded-outlined flx-row flx-align-center modal-option-buttons"}
                        onClick={onAction(ProductActionModalAction.EDIT_TANK_CAPACITY)}
                    >
                        <span className="material-icons-outlined">straighten</span>
                        {AppTranslations.productStockLevel.editProductCapacityButton}
                    </button>
                    <button
                        className={"rounded-outlined flx-row flx-align-center modal-option-buttons"}
                        onClick={onAction(ProductActionModalAction.DELETE)}
                    >
                        <span className="material-icons-outlined">remove_circle_outline</span>
                        {AppTranslations.productStockLevel.deleteProductButton}
                    </button>
                </VeBoxModal.Section>
            </VeBoxModal.Body>
        </VeBoxModal>
    );
}

interface EditProductLevelModalProps {
    title: string | JSX.Element;
    editTextLabel: string;
    isValid: (value: number) => boolean;
    onSubmit: (value: number) => void;
    onCancel: () => void;
}

const EditFieldModal: React.FC<EditProductLevelModalProps> = (props) => {

    const [value, setValue] = React.useState<number>();

    const updateValue = (x) => {
        if (!isNaN(x)) {
            setValue(Number(x));
        }
    }

    const submit = () => {
        props.onSubmit(value);
    }

    return (
        <VeBoxModal>
            <VeBoxModal.Header title={props.title}/>
            <VeBoxModal.Section>
                <div className={"flx-row flx-align-center"}>
                    <Display isVisible={props.children}>
                        <div>{props.children}</div>
                        <span className={"material-icons"}>chevron_right</span>
                    </Display>
                    <NumberInput
                        name={"new_value"}
                        value={value}
                        size="small"
                        label={props.editTextLabel}
                        decimalSeparator={getLocaleDecimalSeparator()}
                        onChange={updateValue}
                        styles={INPUT_BORDER_BOX as any}
                    />
                </div>
            </VeBoxModal.Section>
            <VeBoxModal.StandardActions
                canSubmit={props.isValid(value)}
                onSubmit={submit}
                onCancel={props.onCancel}
            />
        </VeBoxModal>
    );
}

interface QuestionModalProps {
    title: string | JSX.Element;
    onYes: () => void;
    onNo: () => void;
}

const QuestionModal: React.FC<QuestionModalProps> = (props) => {
    return (
        <VeBoxModal>
            <VeBoxModal.Header title={props.title}/>
            <VeBoxModal.Section style={{padding: "20px"}}>
                {props.children}
            </VeBoxModal.Section>
            <VeBoxModal.StandardActions
                submitText={AppTranslations.genericWords.yes}
                cancelText={AppTranslations.genericWords.no}
                onSubmit={props.onYes}
                onCancel={props.onNo}
            />
        </VeBoxModal>
    );
}

interface NewSingleValueModalProps {
    title: string | JSX.Element;
    question: string | JSX.Element;
    editTextLabel: string;
    onSubmit: (value: number) => void;
    isValid: (x: number) => boolean;
    onCancel: () => void;
}

const NewSingleValueModal: React.FC<NewSingleValueModalProps> = (props) => {

    const [newValue, setNewValue] = React.useState<number>();

    const updateValue = (x) => {
        if (!isNaN(x)) {
            setNewValue(Number(x));
        }
    };

    const submit = () => props.onSubmit(newValue);

    return (
        <VeBoxModal>
            <VeBoxModal.Header title={props.title}/>
            <VeBoxModal.Section>
                <div style={{marginBottom: 20}}>
                    {props.question}
                </div>
                <div>
                    <NumberInput
                        name={"new_value"}
                        value={newValue}
                        size="small"
                        label={props.editTextLabel}
                        decimalSeparator={getLocaleDecimalSeparator()}
                        onChange={updateValue}
                        styles={INPUT_BORDER_BOX as any}
                    />
                </div>
            </VeBoxModal.Section>
            <VeBoxModal.StandardActions
                canSubmit={props.isValid(newValue)}
                onSubmit={submit}
                onCancel={props.onCancel}
            />
        </VeBoxModal>
    );
}

function getProductStocks(equipmentGuid: string) {
    return new Promise<ProductStock[]>((res, rej) => {
        RestDataLoader.loadData(
            "equipment/" + equipmentGuid + "/productStocks",
            {},
            (data) => res(data.list as ProductStock[]),
            rej
        );
    })
}

function createProductStock(equipmentGuid: string, product: ProductStock) {
    return new Promise<ProductStock>((res, rej) => {
        RestDataLoader.putJSON(
            "equipment/" + equipmentGuid + "/productStocks/" + product.productStockLevelInfo.guid,
            {
                ...product,
                configured: true
            },
            (data) => res(data),
            rej
        )
    });
}

function productStockPath(path: string, defaultValue?: string) {
    return (equipmentGuid: string, productGuid: string, value: string = defaultValue) => new Promise<ProductStock>((res, rej) => {
        RestDataLoader.patchJSON(
            "equipment/" + equipmentGuid + "/productStocks/" + productGuid,
            {
                "list": [
                    {
                        "op": "replace",
                        "path": path,
                        "value": value ?? defaultValue,
                    }
                ]
            },
            (data) => res(data),
            rej
        )
    });
}

const toggleProductMonitoring = productStockPath("/isConfigured");

function refillProduct(equipmentGuid: string, productGuid: string, refill: number) {
    return new Promise<ProductStock>((res, rej) => {
        RestDataLoader.postJSON(
            "equipment/" + equipmentGuid + "/productStocks/" + productGuid + "/refill",
            {
                refill,
            },
            (data) => res(data),
            rej
        )
    });
}

const updateProductCurrentStockLevel = productStockPath("/stockLevel");

const sendStockLevelFeedback = productStockPath("/stockLevelFeedback");

const updateProductTankCapacity = productStockPath("/capacity");

function getUnitOfMeasureLabel(unit: UnitOfMeasure) {
    switch (unit) {
        case UnitOfMeasure.LITERS: {
            return AppTranslations.productStockLevel.litersLong;
        }
        default: {
            return unit;
        }
    }
}

function brightness(colorHex: string) {
    const red = Number.parseInt(colorHex.slice(1, 3), 16);
    const green = Number.parseInt(colorHex.slice(3, 5), 16);
    const blue = Number.parseInt(colorHex.slice(5, 7), 16);

    return red * 0.299 + green * 0.587 + blue * 0.114;
}

function insertHTMLInString(str: string, htmlElements: (JSX.Element | string)[]): JSX.Element {
    const result: (JSX.Element | string)[] = [];

    let prevOffset = 0;
    str.replace(/{(.*?)}/g, (match, key, offset) => {

        const prevStr = str.substring(prevOffset, offset);
        result.push(prevStr);
        prevOffset = offset + match.length;
        result.push(htmlElements?.[key]);
        return match;
    });

    result.push(str.substring(prevOffset));

    return (
        <> {result} </>
    );
}

function formatNumber(x: number) {
    return x % 1 === 0 ? x : x.toFixed(2).replace(".", getLocaleDecimalSeparator());
}